<script lang="ts">
  import Flash, { type FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { onChange } from '#app/lib/svelte/svelte'
  import type { EntitiesSortFunction } from '#app/types/entity'
  import SortEntitiesBy from '#entities/components/layouts/sort_entities_by.svelte'
  import { hasSelectedLang } from '#entities/components/lib/claims_helpers'
  import { getLangEntities, getPublishersEntities, getPublicationYears, hasPublisher, hasPublicationYear, type LangEntitiesLabel, type PublishersLabels, type WorkEditionFilters } from '#entities/components/lib/editions_list_actions_helpers'
  import type { SerializedEntity } from '#entities/lib/entities'
  import Spinner from '#general/components/spinner.svelte'
  import type { SerializedItemWithUserData } from '#inventory/lib/items'
  import type { EntityUri } from '#server/types/entity'
  import { getCurrentLang, i18n, I18n } from '#user/lib/i18n'
  import type { WikimediaLanguageCode } from 'wikibase-sdk'

  let {
    allEditions,
    waitingForItems,
    workEditionFilters = $bindable(),
    entitiesSortFunction = $bindable(),
    displayedEditionsCount,
  }: {
    allEditions: SerializedEntity[]
    waitingForItems?: Promise<SerializedItemWithUserData[]>
    workEditionFilters: WorkEditionFilters
    entitiesSortFunction: EntitiesSortFunction
    displayedEditionsCount: number
  } = $props()

  let flash: FlashState | undefined = $state()

  workEditionFilters.selectedLang = getCurrentLang()
  workEditionFilters.selectedPublisher = 'all'

  let editionsLangs: WikimediaLanguageCode[] | undefined = $state()
  let langEntitiesLabel: LangEntitiesLabel | undefined = $state()
  let waitingForLangEntities: Promise<void> | undefined = $state()
  $effect(() => {
    onChange(allEditions, () => {
      waitingForLangEntities = getLangEntities(allEditions)
        .then(res => ({ editionsLangs, langEntitiesLabel } = res))
        .then(refreshFilters)
        .catch(err => flash = err)
    })
  })

  let publishersUris: EntityUri[] | undefined = $state()
  let publishersLabels: PublishersLabels | undefined = $state()
  let someEditionsHaveNoPublisher: boolean | undefined = $state()
  let waitingForPublishersEntities: Promise<void> | undefined = $state()
  $effect(() => {
    onChange(allEditions, () => {
      waitingForPublishersEntities = getPublishersEntities(allEditions)
        .then(res => ({ publishersUris, publishersLabels, someEditionsHaveNoPublisher } = res))
        .then(refreshFilters)
        .catch(err => flash = err)
    })
  })

  const publicationYearsData = $derived(getPublicationYears(allEditions))
  const publicationYears = $derived(publicationYearsData.publicationYears)
  const someEditionsHaveNoPublicationDate = $derived(publicationYearsData.someEditionsHaveNoPublicationDate)

  function unselectEmptyFilter () {
    if (allEditions && getLangEditionsCount(workEditionFilters.selectedLang) === 0) {
      workEditionFilters.selectedLang = 'all'
    }
  }

  function refreshFilters () {
    if (langEntitiesLabel) {
      workEditionFilters.selectedLangLabel = langEntitiesLabel[workEditionFilters.selectedLang]
    }
    if (publishersLabels) {
      if (workEditionFilters.selectedPublisher === 'unknown') {
        workEditionFilters.selectedPublisherLabel = `${I18n('unknown publisher')}`
      } else {
        workEditionFilters.selectedPublisherLabel = publishersLabels[workEditionFilters.selectedPublisher]
      }
    }
  }

  const getLangEditionsCount = lang => allEditions.filter(hasSelectedLang(lang)).length
  const getPublisherCount = uri => allEditions.filter(hasPublisher(uri)).length
  const getPublicationYearCount = year => allEditions.filter(hasPublicationYear(year)).length

  $effect(refreshFilters)
  $effect(unselectEmptyFilter)
</script>

<div class="filters menu">
  <span class="filters-header">{i18n('Filter by')}</span>

  {#await waitingForLangEntities}
    <Spinner />
  {:then}
    {#if editionsLangs?.length > 0}
      <div class="filter">
        <label for="language-filter">{I18n('language')}</label>
        <select
          id="language-filter"
          name="language"
          bind:value={workEditionFilters.selectedLang}
          class:filtering={workEditionFilters.selectedLang !== 'all'}
        >
          <option value="all">{I18n('all languages')} ({allEditions.length})</option>
          {#each editionsLangs as lang (lang)}
            <option value={lang}>{lang} - {langEntitiesLabel[lang]} ({getLangEditionsCount(lang)})</option>
          {/each}
        </select>
        {#if workEditionFilters.selectedLang !== 'all'}
          <button
            title={I18n('reset filter')}
            aria-controls="language-filter"
            onclick={() => workEditionFilters.selectedLang = 'all'}
          >{@html icon('close')}</button>
        {/if}
      </div>
    {/if}
  {/await}

  {#await waitingForPublishersEntities}
    <Spinner />
  {:then}
    {#if publishersUris?.length > 0}
      <div class="filter">
        <label for="publisher-filter">{I18n('publisher')}</label>
        <select
          id="publisher-filter"
          name="publisher"
          bind:value={workEditionFilters.selectedPublisher}
          class:filtering={workEditionFilters.selectedPublisher !== 'all'}
        >
          <option value="all">{I18n('all publishers')} ({allEditions.length})</option>
          {#each publishersUris as uri (uri)}
            <option value={uri}>{publishersLabels[uri]} ({getPublisherCount(uri)})</option>
          {/each}
          {#if someEditionsHaveNoPublisher}
            <option value="unknown">{I18n('unknown')} ({getPublisherCount('unknown')})</option>
          {/if}
        </select>
        {#if workEditionFilters.selectedPublisher !== 'all'}
          <button
            title={I18n('reset filter')}
            aria-controls="publisher-filter"
            onclick={() => workEditionFilters.selectedPublisher = 'all'}
          >{@html icon('close')}</button>
        {/if}
      </div>
    {/if}
  {/await}

  <div class="filter">
    <label for="publication-year-filter">{I18n('publication year')}</label>
    <select
      id="publication-year-filter"
      name="publication-year"
      bind:value={workEditionFilters.selectedPublicationYear}
      class:filtering={workEditionFilters.selectedPublicationYear !== 'all'}
    >
      <option value="all">{I18n('any year')} ({allEditions.length})</option>
      {#each publicationYears as year (year)}
        <option value={year}>{year} ({getPublicationYearCount(year)})</option>
      {/each}
      {#if someEditionsHaveNoPublicationDate}
        <option value="unknown">{I18n('unknown')} ({getPublicationYearCount('unknown')})</option>
      {/if}
    </select>
    {#if workEditionFilters.selectedPublicationYear !== 'all'}
      <button
        title={I18n('reset filter')}
        aria-controls="publication-year-filter"
        onclick={() => workEditionFilters.selectedPublicationYear = 'all'}
      >{@html icon('close')}</button>
    {/if}
  </div>

  <!-- TODO: add distribution format (wdt:P437) filters -->
</div>

<div class="sort-selector-wrapper menu">
  <!-- Needs to be initialized in any case, as it is responsible for initiating entitiesSortFunction -->
  <SortEntitiesBy
    sortingType="edition"
    bind:entitiesSortFunction
    entitiesCount={displayedEditionsCount}
    {waitingForItems}
  />
</div>

<Flash state={flash} />

<style lang="scss">
  @import "general/scss/utils";
  .sort-selector-wrapper:not(:empty){
    width: 100%;
    margin-block-start: 0.5em;
    :global(.sort-selector), :global(label){
      margin: 0 1em;
      margin-inline-start: 0;
      font-size: 100%;
      text-align: start;
    }
  }
  .menu:not(:empty){
    background-color: $light-grey;
    padding: 0.5em;
  }
  .filters{
    align-self: stretch;
  }
  .filters-header{
    margin: 0.5em 0;
    align-self: flex-end;
    color: $label-grey;
  }
  .filter{
    margin: 0 1em;
    position: relative;
    select{
      padding-inline-end: 2em;
    }
    label{
      margin-inline-start: 0.8em;
      margin-block-end: 0.2em;
    }
    button{
      position: absolute;
      inset-inline-end: 0.1em;
      inset-block-end: 0.1em;
      block-size: 2.1em;
      inline-size: 2em;
      @include bg-hover(white);
      padding: 0;
    }
  }
  select.filtering{
    border-color: $glow;
  }
  /* Large screens */
  @media screen and (width >= $smaller-screen){
    .filters{
      @include display-flex(row, center, flex-start);
    }
  }
</style>
